import { TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import { useCallback, useMemo } from 'react';

import TextInput from 'src/components/TextInput';
import PopUp from 'src/components/design-system/PopUp';
import { CanManageOfflineSuppliers } from 'src/constants/permissionsList';
import usePermission from 'src/hooks/usePermissions';
import { getBgColor, getIcon } from 'src/utils/icons';
import { useContextSelector } from 'use-context-selector';
import PopUpContentWrapper from '../../../components/PopUpContentWrapper';
import { InvoiceContext } from '../InvoiceDetailsContext';

const SupplierName = () => {
  const invoice = useContextSelector(InvoiceContext, (value) => value.invoice);
  const handleInputChange = useContextSelector(InvoiceContext, (value) => value.handleInputChange);
  const errors = useContextSelector(InvoiceContext, (value) => value.errors);
  const warnings = useContextSelector(InvoiceContext, (value) => value.warnings);
  const setSupplierNameDirty = useContextSelector(
    InvoiceContext,
    (value) => value.setSupplierNameDirty
  );
  const isInvoiceEditDisabled = useContextSelector(
    InvoiceContext,
    (value) => value.isInvoiceEditDisabled
  );
  const getLinkIcon = useContextSelector(InvoiceContext, (value) => value.getLinkIcon);
  const ApIdandExternalSupplierNametooltipMsg = useContextSelector(
    InvoiceContext,
    (value) => value.ApIdandExternalSupplierNametooltipMsg
  );

  const canManageOfflineSuppliers = usePermission({ permissions: CanManageOfflineSuppliers });

  const hasWarning = useMemo(
    () => !!warnings.externalSupplierName.length,
    [warnings.externalSupplierName.length]
  );

  const hasError = useMemo(
    () => !!errors.externalSupplierName.length,
    [errors.externalSupplierName.length]
  );

  const handleSupplierNameChange = useCallback(
    (e: TextBoxChangeEvent) => {
      handleInputChange('externalSupplier')(e);
      setSupplierNameDirty();
    },
    [handleInputChange, setSupplierNameDirty]
  );
  const title = useMemo(
    () => (invoice?.externalSupplier === null ? '' : invoice?.externalSupplier),
    [invoice?.externalSupplier]
  );

  return (
    <PopUp
      content={
        <>
          <PopUpContentWrapper type="Error" contents={errors.externalSupplierName} />
          <PopUpContentWrapper type="Warning" contents={warnings.externalSupplierName} />
        </>
      }
      show={hasWarning || hasError}
      popupAlign={{ horizontal: 'center', vertical: 'bottom' }}
      anchorAlign={{ horizontal: 'right', vertical: 'top' }}
    >
      <div
        className={`p-1 ${getBgColor(
          errors.externalSupplierName,
          warnings.externalSupplierName
        )} text-[13px]`}
        title={!hasError && !hasWarning ? invoice?.externalSupplier || '' : ''}
      >
        <div className="font-bold pb-1 flex flex-row">
          <p className="flex items-center text-elipsis whitespace-nowrap">
            External Supplier Name
            <span title={ApIdandExternalSupplierNametooltipMsg}>{getLinkIcon()}</span>
            <span className="px-2">
              {getIcon(errors.externalSupplierName, warnings.externalSupplierName)}
            </span>
          </p>
        </div>
        {isInvoiceEditDisabled || !canManageOfflineSuppliers ? (
          <p>{invoice?.externalSupplier}</p>
        ) : (
          <TextInput
            value={title}
            title={title}
            onChange={handleSupplierNameChange}
            valid={!hasError}
          />
        )}
      </div>
    </PopUp>
  );
};

export default SupplierName;
